import { deleteClauseById } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { DestructiveDialogProps } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog.types";

import { Delete } from "@mui/icons-material";
import { ListItemIcon, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import React from "react";

interface IMenuActionItem {
    /**
     * The label of the action
     */
    label: string;
    /**
     * The icon of the action
     */
    icon?: React.ReactNode;
    /**
     * Whether the action is disabled
     */
    disabled?: boolean;
    /**
     * The action to perform when the item is clicked
     */
    onClick: () => void;
}

interface IProps {
    /**
     * The selected clause IDs
     */
    selectedClauseIds: number[];
}

/**
 * The toolbar actions for the clauses table
 */
export const ClausesTableToolbarActions = ({ selectedClauseIds }: IProps) => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const dialogProps: DestructiveDialogProps = {
        itemIds: selectedClauseIds,
        subject: "clauses",
        mutationFn: deleteClauseById,
        onSucceeded: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clauses"],
            });
        },
    };

    const menuActionItems: IMenuActionItem[] = [
        {
            label: "Delete Selected",
            icon: <Delete fontSize="small" />,
            disabled: selectedClauseIds.length === 0,
            onClick: async () => {
                // Close the menu
                handleClose();

                // Open the destructive dialog
                await dialogs.open(DestructiveDialog, dialogProps);
            },
        },
    ];

    return (
        <>
            <Button
                type="button"
                variant="contained"
                color="tertiary"
                disableElevation
                sx={{ borderRadius: 22 }}
                onClick={handleClick}
            >
                Actions
            </Button>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: { minWidth: 280, mt: 1.5 },
                        variant: "outlined",
                    },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {menuActionItems.map(
                    ({ onClick, disabled, icon, label }, index) => (
                        <MenuItem
                            key={index}
                            onClick={onClick}
                            disabled={disabled}
                        >
                            {icon && <ListItemIcon>{icon}</ListItemIcon>}
                            {label}
                        </MenuItem>
                    )
                )}
            </Menu>
        </>
    );
};
